/**
 * HubSpot deep-link URL builder (ADR-169, Stage 1).
 *
 * Builds links into Flat Rock Global's HubSpot portal — no API access, no
 * credentials. The portal ID is non-secret (it appears in every HubSpot URL);
 * access control is entirely HubSpot's own login wall.
 *
 * Two URL shapes:
 *
 *   With a known contact record ID (linked persons, Stage 2):
 *     https://app.hubspot.com/contacts/{portalId}/record/0-1/{contactId}
 *
 *   Email-only fallback — the contacts list pre-filtered to the email,
 *   one click from the record:
 *     https://app.hubspot.com/contacts/{portalId}/objects/0-1/views/all/list
 *       ?query={email}
 *
 * Configuration via Vite env var (set at build time):
 *   VITE_HUBSPOT_PORTAL_ID — the production portal's Hub ID
 *
 * Returns null when unconfigured; call sites hide the button. Env is read
 * inside the functions (not module scope) so vi.stubEnv works in tests.
 */

import {
  buildHubSpotContactRecordUrl,
  HUBSPOT_CONTACT_OBJECT_TYPE,
  type HubSpotContactSuggestion,
} from "@event-planner/shared";

/** HubSpot's brand orange — the linked-state sprocket color (Stage 2). */
export const HUBSPOT_ORANGE = "#FF7A59";

const NUMERIC_ID = /^\d+$/;

function configuredPortalId(): string | null {
  const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID?.trim();
  return portalId && NUMERIC_ID.test(portalId) ? portalId : null;
}

export function isHubSpotConfigured(): boolean {
  return configuredPortalId() !== null;
}

interface HubSpotContactLinkInput {
  email: string;
  /** Direct record link when known (Stage 2). Falls back to email-query list URL. */
  contactId?: string | null;
}

/**
 * Builds a link to a person's contact in HubSpot, or null if HubSpot is not
 * configured for this admin build.
 */
export function buildHubSpotContactUrl({
  email,
  contactId,
}: HubSpotContactLinkInput): string | null {
  const portalId = configuredPortalId();
  if (!portalId) return null;

  if (contactId) {
    return buildHubSpotContactRecordUrl(portalId, contactId);
  }

  return (
    `https://app.hubspot.com/contacts/${portalId}/objects/${HUBSPOT_CONTACT_OBJECT_TYPE}` +
    `/views/all/list?query=${encodeURIComponent(email)}`
  );
}

// ── Review-first pull computation (ADR-172 revision) ─────────────────
//
// Linking is organizer-driven: the match dialog picks a contact, the
// helpers below compute what the CRM offers against what the edit form
// currently holds, the values land in the form marked dirty, and
// nothing persists until the organizer saves. Pure functions — the
// token-bearing fetches stay server-side (searchHubSpotContacts /
// getHubSpotContact callables).

/** Profile fields subject to the fill/conflict rule. */
export const HUBSPOT_PULL_FIELDS = [
  "title",
  "organization",
  "linkedIn",
  "phone",
  "workCity",
  "workState",
] as const;

export type HubSpotPullField = (typeof HUBSPOT_PULL_FIELDS)[number];

export const HUBSPOT_PULL_FIELD_LABELS: Record<HubSpotPullField, string> = {
  title: "Title",
  organization: "Organization",
  linkedIn: "LinkedIn",
  phone: "Phone",
  workCity: "Work city",
  workState: "Work state",
};

/** A field where both the form and the CRM have different values. */
export interface HubSpotFieldConflict {
  field: HubSpotPullField;
  /** Current value in the form. */
  ours: string;
  /** Value on the HubSpot contact. */
  theirs: string;
}

export interface HubSpotPullResult {
  /** Empty-in-form fields the CRM can fill, with the CRM values. */
  fills: Partial<Record<HubSpotPullField, string>>;
  /** Both-sides-differ fields for the reconcile dialog. */
  conflicts: HubSpotFieldConflict[];
}

/** Trim to null — CRM and form values are compared in this form. */
function norm(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Computes the pull set for a picked contact against the form's current
 * values: empty fields fill, matching values are untouched, differing
 * values become conflicts for the organizer to reconcile. Nothing is
 * ever overwritten without an explicit choice.
 */
export function buildHubSpotPull(
  suggestion: HubSpotContactSuggestion,
  current: Partial<Record<HubSpotPullField, string | null | undefined>>,
): HubSpotPullResult {
  const crmValues: Record<HubSpotPullField, string | null> = {
    title: norm(suggestion.jobTitle),
    organization: norm(suggestion.company),
    linkedIn: norm(suggestion.linkedIn),
    phone: norm(suggestion.phone),
    workCity: norm(suggestion.city),
    workState: norm(suggestion.state),
  };

  const fills: Partial<Record<HubSpotPullField, string>> = {};
  const conflicts: HubSpotFieldConflict[] = [];
  for (const field of HUBSPOT_PULL_FIELDS) {
    const theirs = crmValues[field];
    if (!theirs) continue;
    const ours = norm(current[field]);
    if (!ours) {
      fills[field] = theirs;
    } else if (ours !== theirs) {
      conflicts.push({ field, ours, theirs });
    }
  }
  return { fills, conflicts };
}

/**
 * The "From HubSpot" block: the contact's LinkedIn bio — the one CRM
 * fact with real who-is-this-person value and no structured home.
 * (FinTrx segmentation attributes — credential, rep registration,
 * vendor dataset — were cut by owner decision, ADR-172: they slice CRM
 * lists, they don't brief an organizer.) Returns null when the contact
 * has no bio.
 */
export function buildHubSpotNotesBlock(
  suggestion: HubSpotContactSuggestion,
): string | null {
  const bio = norm(suggestion.linkedInBio);
  if (!bio) return null;
  const date = new Date().toISOString().slice(0, 10);
  return `— From HubSpot (${date}) —\nLinkedIn bio: ${bio}`;
}

/**
 * Appends the block beneath existing notes. Idempotent against
 * double-apply (confirming the same contact twice before saving): if the
 * block's fact lines are already present, the notes come back unchanged.
 */
export function appendHubSpotNotesBlock(
  currentNotes: string | null | undefined,
  block: string,
): { notes: string; appended: boolean } {
  const existing = norm(currentNotes);
  // Compare on the fact lines — the header carries a date that may vary.
  const factLines = block.split("\n").slice(1).join("\n");
  if (existing?.includes(factLines)) {
    return { notes: existing, appended: false };
  }
  return {
    notes: existing ? `${existing}\n\n${block}` : block,
    appended: true,
  };
}

/**
 * Extracts the contact record ID from a pasted HubSpot URL (the manual
 * link path, Stage 2). Accepts the current record URL shape
 * (`…/record/0-1/{id}`), the legacy shape (`…/contact/{id}`), or a bare
 * numeric ID. Returns null when nothing ID-like is found.
 */
export interface HubSpotContactReferenceResult {
  contactId: string | null;
  error: string | null;
}

/**
 * Parses a bare record ID or a HubSpot contact URL and, when configured,
 * rejects URLs copied from another portal. Free-form text is not an error —
 * the match dialog treats it as a normal search query.
 */
export function parseHubSpotContactReference(
  input: string,
): HubSpotContactReferenceResult {
  const trimmed = input.trim();
  if (!trimmed) return { contactId: null, error: null };

  if (NUMERIC_ID.test(trimmed)) {
    return { contactId: trimmed, error: null };
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { contactId: null, error: null };
  }

  if (url.protocol !== "https:" || url.hostname !== "app.hubspot.com") {
    return {
      contactId: null,
      error: "Paste a contact URL from app.hubspot.com, or enter a contact record ID.",
    };
  }

  const segments = url.pathname.split("/").filter(Boolean);
  if (segments[0] !== "contacts" || !NUMERIC_ID.test(segments[1] ?? "")) {
    return { contactId: null, error: "That is not a HubSpot contact record URL." };
  }

  const portalId = segments[1];
  const expectedPortalId = configuredPortalId();
  if (expectedPortalId && portalId !== expectedPortalId) {
    return {
      contactId: null,
      error: `That contact belongs to HubSpot portal ${portalId}, not the configured portal ${expectedPortalId}.`,
    };
  }

  let contactId: string | null = null;
  if (
    segments[2] === "record" &&
    segments[3] === HUBSPOT_CONTACT_OBJECT_TYPE &&
    NUMERIC_ID.test(segments[4] ?? "")
  ) {
    contactId = segments[4];
  } else if (segments[2] === "contact" && NUMERIC_ID.test(segments[3] ?? "")) {
    contactId = segments[3];
  }

  if (!contactId) {
    return { contactId: null, error: "That is not a HubSpot contact record URL." };
  }

  return { contactId, error: null };
}

/** Backward-compatible ID-only wrapper for existing non-dialog consumers. */
export function parseHubSpotContactUrl(input: string): string | null {
  return parseHubSpotContactReference(input).contactId;
}
